/**
 * Alpine.js Product Filter Component
 * Client-side filtering, sorting, and pagination — no server round-trips.
 * Reads config from window.__productFilterConfig (set by Twig template).
 */
export function productFilter() {
  const config = window.__productFilterConfig || {};

  return {
    // Product metadata for filtering/sorting
    products: config.products || [],

    // Category hierarchy maps
    categoryChildren: config.categoryChildren || {},
    categoryAncestors: config.categoryAncestors || {},
    slugToId: config.slugToId || {},

    // Pagination
    perPage: config.perPage || 9,

    // Filter state
    activeCatSlug: '',
    selectedBrand: '',
    selectedType: '',
    sort: 'popularity',
    page: 1,
    mobileFilterOpen: false,

    init() {
      // Read initial state from URL
      const params = new URLSearchParams(window.location.search);
      this.activeCatSlug = params.get('cat') || '';
      this.selectedBrand = params.get('brand') || '';
      this.selectedType = params.get('type') || '';
      this.sort = params.get('sort') || 'popularity';
      this.page = parseInt(params.get('page'), 10) || 1;

      // Handle browser back/forward
      window.addEventListener('popstate', () => {
        const p = new URLSearchParams(window.location.search);
        this.activeCatSlug = p.get('cat') || '';
        this.selectedBrand = p.get('brand') || '';
        this.selectedType = p.get('type') || '';
        this.sort = p.get('sort') || 'popularity';
        this.page = parseInt(p.get('page'), 10) || 1;
      });
    },

    // --- Category helpers ---

    /** Get all descendant category IDs (recursive) */
    getDescendantIds(catId) {
      const children = this.categoryChildren[catId] || [];
      let desc = [...children];
      for (const child of children) {
        desc.push(...this.getDescendantIds(child));
      }
      return desc;
    },

    /** Is catId the active category or one of its ancestors? */
    isActiveOrAncestor(catId) {
      if (!this.activeCatSlug) return false;
      const activeId = this.slugToId[this.activeCatSlug];
      if (!activeId) return false;
      if (catId == activeId) return true;
      return (this.categoryAncestors[activeId] || []).includes(catId);
    },

    // --- Actions ---

    selectCategory(slug) {
      this.activeCatSlug = slug;
      this.selectedBrand = '';
      this.selectedType = '';
      this.page = 1;
      this.mobileFilterOpen = false;
      this.updateUrl();
    },

    clearFilters() {
      this.selectedType = '';
      this.selectedBrand = '';
      this.sort = 'popularity';
      this.page = 1;
      this.updateUrl();
    },

    goToPage(p) {
      this.page = p;
      this.updateUrl();
      document.getElementById('productsFilter')?.scrollIntoView({ behavior: 'smooth' });
    },

    // --- Computed properties ---

    /** Filtered + sorted product list */
    get filteredProducts() {
      let result = this.products;

      // Filter by category (+ descendants)
      if (this.activeCatSlug) {
        const activeId = this.slugToId[this.activeCatSlug];
        if (activeId) {
          const targetIds = new Set([activeId, ...this.getDescendantIds(activeId)]);
          result = result.filter(p => p.catIds.some(id => targetIds.has(id)));
        }
      }

      // Filter by brand
      if (this.selectedBrand) {
        result = result.filter(p => p.brand === this.selectedBrand);
      }

      // Filter by type
      if (this.selectedType) {
        result = result.filter(p => p.type === this.selectedType);
      }

      // Sort (create new array to avoid mutating)
      const sorted = [...result];
      if (this.sort === 'popularity') {
        sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
      } else if (this.sort === 'dateCreatedOld') {
        sorted.sort((a, b) => a.date - b.date);
      } else {
        sorted.sort((a, b) => b.date - a.date);
      }

      // Featured products first (stable — preserves the sort applied above)
      sorted.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

      return sorted;
    },

    /** Products visible on the current page */
    get paginatedProducts() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredProducts.slice(start, start + this.perPage);
    },

    /** Set of visible product IDs for fast lookup */
    get visibleIdSet() {
      return new Set(this.paginatedProducts.map(p => p.id));
    },

    get totalPages() {
      return Math.ceil(this.filteredProducts.length / this.perPage);
    },

    get totalProducts() {
      return this.filteredProducts.length;
    },

    /** Check if a product card should be visible */
    isVisible(id) {
      return this.visibleIdSet.has(id);
    },

    /** Get CSS order for a product card (for correct visual ordering) */
    getOrder(id) {
      const idx = this.paginatedProducts.findIndex(p => p.id === id);
      return idx >= 0 ? idx : 9999;
    },

    /** Available brands for current category selection */
    get availableBrands() {
      let products = this.products;
      if (this.activeCatSlug) {
        const activeId = this.slugToId[this.activeCatSlug];
        if (activeId) {
          const targetIds = new Set([activeId, ...this.getDescendantIds(activeId)]);
          products = products.filter(p => p.catIds.some(id => targetIds.has(id)));
        }
      }
      const seen = new Set();
      return products.reduce((acc, p) => {
        if (p.brand && !seen.has(p.brand)) {
          seen.add(p.brand);
          acc.push({ slug: p.brand, name: p.brandName });
        }
        return acc;
      }, []);
    },

    /** Available types for current category selection */
    get availableTypes() {
      let products = this.products;
      if (this.activeCatSlug) {
        const activeId = this.slugToId[this.activeCatSlug];
        if (activeId) {
          const targetIds = new Set([activeId, ...this.getDescendantIds(activeId)]);
          products = products.filter(p => p.catIds.some(id => targetIds.has(id)));
        }
      }
      const seen = new Set();
      return products.reduce((acc, p) => {
        if (p.type && !seen.has(p.type)) {
          seen.add(p.type);
          acc.push({ handle: p.type, name: p.typeName });
        }
        return acc;
      }, []);
    },

    hasActiveFilters() {
      return this.selectedType || this.selectedBrand || this.sort !== 'popularity';
    },

    // --- URL state ---

    updateUrl() {
      const params = new URLSearchParams();
      if (this.activeCatSlug) params.set('cat', this.activeCatSlug);
      if (this.selectedBrand) params.set('brand', this.selectedBrand);
      if (this.selectedType) params.set('type', this.selectedType);
      if (this.sort && this.sort !== 'popularity') params.set('sort', this.sort);
      if (this.page > 1) params.set('page', String(this.page));

      const qs = params.toString();
      const url = qs ? window.location.pathname + '?' + qs : window.location.pathname;
      history.pushState({}, '', url);

      // Save to sessionStorage for filter-state-manager compatibility
      try {
        sessionStorage.setItem('productFilterState', JSON.stringify({
          cat: this.activeCatSlug,
          type: this.selectedType,
          brand: this.selectedBrand,
          sort: this.sort,
          page: this.page
        }));
      } catch(e) { /* ignore */ }
    },
  };
}
